import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo, useRef, useState } from "react";

import StepBar from "./StepBar";
import {
  calculerCriticite,
  CRITICITE_PRESENTATIONS,
} from "./criticiteHelper";

const GRAVITE_OPTIONS = [
  { label: "1 — Mineure (sans impact opérationnel)", value: "1" },
  { label: "2 — Modérée (impact possible processus)", value: "2" },
  { label: "3 — Sérieuse (impact réel / Non respect KPI)", value: "3" },
  { label: "4 — Critique (impact légal / bénéficiaires)", value: "4" },
];

const FREQUENCE_OPTIONS = [
  { label: "1 — Jamais (aucun antécédent)", value: "1" },
  { label: "2 — Rare (1 fois dans l'année)", value: "2" },
  { label: "3 — Prévisible (plusieurs fois par an)", value: "3" },
  {
    label:
      "4 — Récurrent (écart récurrent sur la prestation durant les 3 derniers mois glissants)",
    value: "4",
  },
];

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

const exigenceLabel = (exigence) => {
  const code = String(exigence?.exigence_code || "").trim();
  const name = String(exigence?.exigence_name || "").trim();
  return [code ? `[${code}]` : "", name].filter(Boolean).join(" ");
};

const fieldLabelSx = {
  color: "#536b91",
  display: "block",
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.06em",
  mb: 0.55,
  textTransform: "uppercase",
  "& .MuiFormLabel-asterisk": { color: "#d32f2f" },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "8px",
    fontSize: 13,
  },
};

const OuvertureStep = ({
  exigences,
  exigencesError,
  exigencesLoading,
  formData,
  navigationEnabled,
  onBack,
  onChange,
  onReset,
  onRetryExigences,
  onSave,
  onStepChange,
  saving,
}) => {
  const [errors, setErrors] = useState({});
  const exigenceRef = useRef(null);
  const descriptionRef = useRef(null);
  const graviteRef = useRef(null);
  const frequenceRef = useRef(null);
  const selectedExigence = useMemo(
    () =>
      exigences.find((item) =>
        sameId(item.exigence_id, formData.exigence_id)
      ) || null,
    [exigences, formData.exigence_id]
  );

  const criticitePresentation =
    CRITICITE_PRESENTATIONS[formData.criticite] || null;

  const handleMatrixChange = (fieldName, value) => {
    const gravite = fieldName === "gravite_id" ? value : formData.gravite_id;
    const frequence =
      fieldName === "frequence_id" ? value : formData.frequence_id;

    onChange(fieldName, value);
    setErrors((current) => ({
      ...current,
      [fieldName]: undefined,
      criticite: undefined,
    }));

    if (!gravite || !frequence) {
      onChange("criticite", "");
      return;
    }

    onChange(
      "criticite",
      calculerCriticite(Number(gravite), Number(frequence))
    );
  };

  const handleExigenceChange = (option) => {
    onChange("exigence_id", option?.exigence_id ?? "");
    setErrors((current) => ({ ...current, exigence_id: undefined }));
  };

  const handleDescriptionChange = (value) => {
    onChange("description_exigence", value);
    setErrors((current) => ({ ...current, description_exigence: undefined }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.exigence_id) {
      nextErrors.exigence_id = "Le type d’exigence est obligatoire.";
    }
    if (!String(formData.description_exigence || "").trim()) {
      nextErrors.description_exigence = "L’exigence précise est obligatoire.";
    }
    if (!formData.gravite_id) nextErrors.gravite_id = "La gravité est obligatoire.";
    if (!formData.frequence_id) {
      nextErrors.frequence_id = "La fréquence est obligatoire.";
    }
    if (!formData.criticite) {
      nextErrors.criticite = "La criticité doit être calculée.";
    }

    setErrors(nextErrors);
    const firstInvalid = [
      ["exigence_id", exigenceRef],
      ["description_exigence", descriptionRef],
      ["gravite_id", graviteRef],
      ["frequence_id", frequenceRef],
    ].find(([name]) => nextErrors[name]);
    firstInvalid?.[1]?.current?.focus();

    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = (destination) => {
    if (!saving && validate()) onSave(destination);
  };

  const handleReset = () => {
    setErrors({});
    onReset();
  };

  return (
    <Box
      aria-label="Étape d’ouverture de la fiche NC"
      role="region"
      sx={{
        backgroundColor: "#f1f5fb",
        minHeight: "calc(100vh - 56px)",
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <StepBar
        activeStep={1}
        navigationEnabled={navigationEnabled}
        onStepChange={onStepChange}
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #cbd8e6",
          borderRadius: "12px",
          mt: 2.5,
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            borderBottom: "1px solid #d5e0eb",
            display: "flex",
            gap: 1.5,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: "#f2effa",
              borderRadius: "9px",
              color: "#7966a4",
              display: "flex",
              height: 34,
              justifyContent: "center",
              width: 34,
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box>
            <Typography color="#10263d" fontSize={14} fontWeight={800}>
              Étape 2 — Ouverture de la Fiche NC (FNC)
            </Typography>
            <Typography color="#60758a" fontSize={11}>
              Qualifier officiellement la non-conformité
            </Typography>
          </Box>
        </Box>

        <Alert
          icon={<span aria-hidden="true">💡</span>}
          severity="warning"
          sx={{
            backgroundColor: "#fffbed",
            border: "1px solid #f2ce55",
            color: "#8a5100",
            mt: 2,
          }}
        >
          Renseignez le type d&apos;exigence concernée, puis calculez le niveau de
          criticité selon la grille Gravité × Fréquence.
        </Alert>

        <Grid container spacing={1.7} sx={{ mt: 0.2 }}>
          <Grid item md={6} xs={12}>
            <FormLabel required sx={fieldLabelSx}>
              Type d&apos;exigence non respectée
            </FormLabel>
            <Autocomplete
              disabled={exigencesLoading || Boolean(exigencesError)}
              getOptionLabel={exigenceLabel}
              isOptionEqualToValue={(option, value) =>
                sameId(option.exigence_id, value.exigence_id)
              }
              loading={exigencesLoading}
              loadingText="Chargement des exigences…"
              noOptionsText="Aucun type d’exigence disponible"
              onChange={(_, option) => handleExigenceChange(option)}
              options={exigences}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(exigencesError || errors.exigence_id)}
                  helperText={errors.exigence_id || exigencesError || " "}
                  inputRef={exigenceRef}
                  placeholder="Choisir un type d’exigence"
                  size="small"
                  sx={fieldSx}
                />
              )}
              value={selectedExigence}
            />
            {exigencesError && (
              <Button
                onClick={onRetryExigences}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Réessayer
              </Button>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormLabel id="description-exigence-label" required sx={fieldLabelSx}>
              Exigence précise non respectée
            </FormLabel>
            <Box
              aria-labelledby="description-exigence-label"
              aria-invalid={Boolean(errors.description_exigence)}
              component="textarea"
              onChange={(event) => handleDescriptionChange(event.target.value)}
              placeholder="Décrivez précisément l’exigence qui n’a pas été respectée"
              rows={3}
              sx={{
                backgroundColor: "#fff",
                border: `1px solid ${
                  errors.description_exigence ? "#d32f2f" : "#c7d3e4"
                }`,
                borderRadius: "8px",
                boxSizing: "border-box",
                color: "#10263d",
                fontFamily: "inherit",
                fontSize: 13,
                lineHeight: 1.5,
                minHeight: 86,
                outline: "none",
                p: 1.4,
                resize: "vertical",
                transition: "border-color 150ms ease, box-shadow 150ms ease",
                width: "100%",
                "&:hover": { borderColor: "#7890aa" },
                "&:focus": {
                  borderColor: errors.description_exigence ? "#d32f2f" : "#00a6b6",
                  boxShadow: errors.description_exigence
                    ? "0 0 0 2px rgba(211, 47, 47, 0.14)"
                    : "0 0 0 2px rgba(0, 166, 182, 0.16)",
                },
                "&::placeholder": { color: "#8a9aae", opacity: 1 },
              }}
              ref={descriptionRef}
              value={formData.description_exigence}
            />
            <Typography
              color={errors.description_exigence ? "#d32f2f" : "transparent"}
              fontSize={11}
              sx={{ minHeight: 18, mt: 0.4 }}
            >
              {errors.description_exigence || "Aucune erreur"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ ...fieldLabelSx, mb: 0.8 }}>
              Niveau de criticité
            </Typography>
            <Grid container spacing={1.2}>
              <Grid item md={5} xs={12}>
                <FormLabel required sx={fieldLabelSx}>Gravité (G)</FormLabel>
                <TextField
                  error={Boolean(errors.gravite_id)}
                  fullWidth
                  helperText={errors.gravite_id || " "}
                  inputRef={graviteRef}
                  onChange={(event) =>
                    handleMatrixChange("gravite_id", event.target.value)
                  }
                  select
                  size="small"
                  sx={fieldSx}
                  value={formData.gravite_id}
                >
                  <MenuItem value="">—</MenuItem>
                  {GRAVITE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item md={5} xs={12}>
                <FormLabel required sx={fieldLabelSx}>Fréquence (F)</FormLabel>
                <TextField
                  error={Boolean(errors.frequence_id)}
                  fullWidth
                  helperText={errors.frequence_id || " "}
                  inputRef={frequenceRef}
                  onChange={(event) =>
                    handleMatrixChange("frequence_id", event.target.value)
                  }
                  select
                  size="small"
                  sx={fieldSx}
                  value={formData.frequence_id}
                >
                  <MenuItem value="">—</MenuItem>
                  {FREQUENCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item md={2} xs={12}>
                <FormLabel required sx={fieldLabelSx}>Criticité</FormLabel>
                <TextField
                  disabled
                  fullWidth
                  inputProps={{ style: { textAlign: "center" } }}
                  placeholder="Calcul auto"
                  size="small"
                  sx={{
                    ...fieldSx,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor:
                        criticitePresentation?.color || "#8a9aae",
                      fontWeight: 800,
                    },
                  }}
                  value={criticitePresentation?.label || ""}
                />
                <Typography
                  color={errors.criticite ? "#d32f2f" : "transparent"}
                  fontSize={11}
                  sx={{ minHeight: 18, mt: 0.4 }}
                >
                  {errors.criticite || "Aucune erreur"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "flex-end",
            mt: 2.5,
          }}
        >
          <Button
            disabled={saving}
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: "none" }}
            variant="outlined"
          >
            Retour
          </Button>
          <Button
            color="error"
            disabled={saving}
            onClick={handleReset}
            startIcon={<RefreshIcon />}
            sx={{ borderColor: "#ff9da3", textTransform: "none" }}
            variant="outlined"
          >
            Réinitialiser saisie
          </Button>
          <Button
            disabled={saving}
            onClick={() => handleSave("registre")}
            startIcon={
              saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />
            }
            sx={{
              backgroundColor: "#00a6b6",
              textTransform: "none",
              "&:hover": { backgroundColor: "#008f9d" },
            }}
            variant="contained"
          >
            Enregistrer la NC
          </Button>
          <Button
            disabled={saving}
            endIcon={saving ? null : <ArrowForwardIcon />}
            sx={{
              backgroundColor: "#00a6b6",
              textTransform: "none",
              "&:hover": { backgroundColor: "#008f9d" },
            }}
            onClick={() => handleSave("curatif")}
            variant="contained"
          >
            Étape suivante
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

OuvertureStep.defaultProps = {
  exigencesError: null,
  navigationEnabled: false,
  onStepChange: () => {},
};

OuvertureStep.propTypes = {
  exigences: PropTypes.arrayOf(
    PropTypes.shape({
      created_at: PropTypes.string,
      exigence_code: PropTypes.string,
      exigence_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
      exigence_name: PropTypes.string,
    })
  ).isRequired,
  exigencesError: PropTypes.string,
  exigencesLoading: PropTypes.bool.isRequired,
  formData: PropTypes.shape({
    criticite: PropTypes.string,
    description_exigence: PropTypes.string,
    exigence_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    frequence_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gravite_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  navigationEnabled: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onRetryExigences: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onStepChange: PropTypes.func,
  saving: PropTypes.bool.isRequired,
};

export default OuvertureStep;
